import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { Dropdown } from "antd";
import dayjs from "dayjs";
import CommonIcons from "@base/assets/icons";
import CommonStyles from "@components/CommonStyles";
import PatientRecordForm from "@components/Booking/PatientRecordForm";
import useToggle from "@base/hooks/useToggle";
import { setAlertMessage } from "@base/store/reducers/appCommonSlice";
import { BookingDraft, BookingStep, withRecord } from "@base/modules/appointment/regularBookingRules";
import { IPatientRecord, IPatientRecordData } from "@base/modules/user/profile/profile.interface";
import profileService from "@base/modules/user/profile/profileService";

interface IProps {
  draft: BookingDraft;
  onNext: (draft: BookingDraft, step?: BookingStep) => void;
}

/** "Select record": the primary record first; a new one is chosen as soon as it is saved. */
export default function ChooseRecordStep({ draft, onNext }: IProps) {
  //! state
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { open: openForm, shouldRender: shouldRenderForm, toggle: toggleForm } = useToggle();
  const [records, setRecords] = useState<IPatientRecordData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  //! function
  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      const resp = await profileService.getByUserKey();

      setRecords(resp.data ?? []);
    } catch (error) {
      setRecords([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sorted = useMemo(
    () => [...records].sort((a, b) => Number(!!b.patient_record?.default_record) - Number(!!a.patient_record?.default_record)),
    [records],
  );

  const choose = (record?: IPatientRecord) => {
    if (record) {
      onNext(withRecord(draft, record));
    }
  };

  const makeDefault = async (record?: IPatientRecord) => {
    if (!record?.code) {
      return;
    }

    try {
      await profileService.setDefault(record.code);
      load();
    } catch (error) {
      dispatch(setAlertMessage({ type: "error", message: t("Notification"), description: t("booking.genericError") }));
    }
  };

  //! render
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end">
        <CommonStyles.ButtonUI ghost type="primary" onClick={toggleForm}>
          + {t("booking.record.addNew")}
        </CommonStyles.ButtonUI>
      </div>

      {isLoading ? <CommonStyles.LoadingUI /> : null}

      {!isLoading && !sorted.length ? (
        <div className="w-full bg-common-background rounded-lg p-4 text-center text-common-subtext text-sm font-normal font-['Inter']">
          {t("booking.record.empty")}
        </div>
      ) : null}

      {sorted.map((item) => {
        const record = item.patient_record;
        const isCurrent = !!record && record.id === draft.record?.id;

        return (
          <div
            key={record?.code}
            onClick={() => choose(record)}
            className={`flex items-center justify-between gap-3 p-4 rounded-xl border cursor-pointer hover:border-common-primary ${
              isCurrent ? "border-common-primary bg-common-primary50" : "border-common-divider bg-common-white"
            }`}
          >
            <div className="flex flex-col gap-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-common-maintext text-base font-semibold font-['Inter'] uppercase">{record?.patient_name}</span>
                {record?.default_record ? (
                  <CommonStyles.TagUI className="bg-common-primary100 text-common-primary border-none" tagName={t("booking.record.primary")} />
                ) : null}
              </div>
              <span className="text-common-subtext text-sm font-['Inter']">
                {[
                  record?.patient_gender === false ? t("appointmentDetail.female") : t("appointmentDetail.male"),
                  record?.patient_dob ? dayjs(record.patient_dob, "YYYY-MM-DD").format("DD/MM/YYYY") : null,
                ]
                  .filter(Boolean)
                  .join(" - ")}
              </span>
            </div>

            {!record?.default_record ? (
              <div onClick={(event) => event.stopPropagation()}>
                <Dropdown
                  trigger={["click"]}
                  menu={{ items: [{ key: "default", label: t("booking.record.makeDefault"), onClick: () => makeDefault(record) }] }}
                >
                  <button type="button" aria-label={t("booking.record.makeDefault")} className="px-2 text-common-subtext text-lg leading-none">
                    ⋯
                  </button>
                </Dropdown>
              </div>
            ) : (
              <CommonIcons.RightOutlined className="text-common-subtext" />
            )}
          </div>
        );
      })}

      {shouldRenderForm && (
        <PatientRecordForm
          open={openForm}
          toggle={toggleForm}
          existingNames={records.map((item) => String(item.patient_record?.patient_name ?? ""))}
          onCreated={(record) => {
            toggleForm();
            choose(record);
          }}
        />
      )}
    </div>
  );
}
